import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'
import { SourceImageCard } from './media/SourceImageCard'
import type { AddToProductImage, AddToProductTextField } from './types'

export interface AddToProductSourcePanelProps {
  images: AddToProductImage[]
  texts?: AddToProductTextField[]
  onAutoFill?: () => void
}

const TAB_ACTIVE =
  'font-medium text-neutral-900 dark:text-neutral-100 border-b-2 border-orange-500'
const TAB_INACTIVE =
  'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'

export function AddToProductSourcePanel({
  images,
  texts,
  onAutoFill,
}: AddToProductSourcePanelProps) {
  const { t } = useTranslation()
  const hasTextSubtab = !!texts && texts.length > 0
  const [tab, setTab] = useState<'text' | 'images'>(hasTextSubtab ? 'text' : 'images')

  return (
    <aside className="bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {t('addToProduct.sidebar.title')}
        </div>
      </div>
      <div
        className="flex border-b border-neutral-200 dark:border-neutral-800"
        role="tablist"
      >
        {hasTextSubtab && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'text'}
            onClick={() => setTab('text')}
            className={`flex-1 py-2 text-sm transition-colors ${
              tab === 'text' ? TAB_ACTIVE : TAB_INACTIVE
            }`}
          >
            {t('addToProduct.sidebar.tabText')}
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'images'}
          onClick={() => setTab('images')}
          className={`flex-1 py-2 text-sm transition-colors ${
            tab === 'images' ? TAB_ACTIVE : TAB_INACTIVE
          }`}
        >
          {t('addToProduct.sidebar.tabImages')}
        </button>
      </div>

      {tab === 'text' && hasTextSubtab ? (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-xs">
          {texts!.map((field) => (
            <div key={field.field}>
              <div className="text-neutral-500 mb-1">{field.label}</div>
              {field.kind === 'bullets' ? (
                <ol className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 list-decimal list-inside space-y-0.5">
                  {field.values.map((b, i) => (
                    <li key={i} className="truncate">
                      {b}
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1.5 line-clamp-3">
                  {field.value}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div
          data-testid="atp-source-images"
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        >
          <button
            type="button"
            onClick={onAutoFill}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t('addToProduct.media.autoFillButton')}
          </button>
          <div className="grid grid-cols-2 gap-2">
            {images.map((img) => (
              <SourceImageCard key={img.id} image={img} />
            ))}
          </div>
        </div>
      )}
    </aside>
  )
}
